<template>
    <Combobox
        :modelValue="modelValue"
        @update:modelValue="(value) => updateValue(value)"
        :multiple="multiple"
        :nullable="nullable"
        v-slot="{ open }"
        :disabled="disabled"
    >
        <div class="w-full flex flex-col gap-y-2 px-[2px]">
            <ComboboxLabel>
                <InputLabel :value="label" :required="required" />
            </ComboboxLabel>
            <div class="relative text-base" ref="reference">
                <ComboboxButton
                    ref="selectButton"
                    :disabled="disabled"
                    :class="[
                        { 'ring-input-focus-border ring-1': open },
                        { 'ring-transparent': !open && !!error },
                        { 'ring-red-500 border-danger': !!error },
                        { 'border-input-border': !error },
                        {
                            'hover:bg-input-hover bg-input-background':
                                !disabled,
                        },
                        {
                            'opacity-50 cursor-not-allowed bg-gray-50':
                                disabled,
                        },
                        'focus:ring-input-focus-border focus:ring-1 focus:outline-none focus:border-input-focus-border',
                        'w-full flex items-center justify-between text-base rounded-input border p-input transition duration-button-hover',
                    ]"
                    tabindex="0"
                >
                    <div
                        v-if="selectedOptions.length == 1"
                        class="flex gap-x-1"
                    >
                        <div
                            v-if="selectedOptions[0].html_icon"
                            v-html="selectedOptions[0].html_icon"
                        />
                        <p
                            class="font-semibold"
                            v-text="selectedOptions[0].name"
                        />
                    </div>
                    <div v-else class="flex gap-x-2 items-center">
                        <p
                            :class="{ 'text-input-muted': !hasValue }"
                            v-text="inputText"
                        />
                        <TextTooltip
                            v-if="selectedOptions.length > 1"
                            :text="
                                selectedOptions
                                    .map((option) => option.name)
                                    .join(', ')
                            "
                        >
                            <PhQuestion
                                class="size-5 text-primary hover:text-primary-hover transition-colors duration-button-hover cursor-help"
                                weight="fill"
                            />
                        </TextTooltip>
                    </div>

                    <PhCaretDown class="h-6 w-6" weight="bold" />
                </ComboboxButton>

                <Teleport to="body">
                    <transition
                        leave-active-class="transition duration-100 ease-in"
                        leave-from-class="opacity-100"
                        leave-to-class="opacity-0"
                    >
                        <ComboboxOptions
                            ref="floating"
                            :style="[floatingStyles, dropDownWidth]"
                            class="absolute shadow-popover border border-input-boder-popover mt-1 bg-white z-popover rounded-input overflow-hidden"
                        >
                            <!-- Search input -->
                            <div class="w-full px-3 my-3 relative">
                                <ComboboxInput
                                    ref="input"
                                    @change="query = $event.target.value"
                                    class="w-full placeholder:text-input-muted rounded-input border border-input-boder focus-style p-input transition-colors duration-200 pr-9"
                                    :placeholder="
                                        searchPlaceholder || $lang.get('Search')
                                    "
                                    autocomplete="off"
                                />
                                <PhMagnifyingGlass
                                    class="absolute right-5 top-0 h-full flex items-center w-6"
                                />
                            </div>

                            <!-- Search results -->
                            <div>
                                <div
                                    class="flex items-center justify-between pl-2 mb-2 pr-5 font-semibold tracking-wide text-sm"
                                >
                                    <p v-text="$lang.get('Search results')" />
                                    <button
                                        v-if="
                                            multiple &&
                                            selectedOptions.length > 0
                                        "
                                        class="text-sm hover:text-input-muted underline transition-colors duration-button-hover"
                                        @click="() => updateValue([])"
                                        v-text="$lang.get('Deselect all')"
                                    />
                                </div>
                                <div class="max-h-72 overflow-y-auto">
                                    <ComboboxOption
                                        v-for="(option, idx) in filteredOptions"
                                        v-slot="{ active, selected }"
                                        :order="idx"
                                        :key="option.value"
                                        :value="option.value"
                                    >
                                        <div
                                            :class="[
                                                {
                                                    'bg-input-active-option text-input-active-option-text':
                                                        selected,
                                                },
                                                {
                                                    'bg-input-hover ':
                                                        !selected && active,
                                                },
                                                'transition-colors duration-200 p-4 border-input-border flex gap-x-1 cursor-pointer',
                                            ]"
                                        >
                                            <span
                                                v-if="option.html_icon"
                                                v-html="option.html_icon"
                                            />
                                            <p v-text="option.name" />
                                        </div>
                                    </ComboboxOption>
                                    <p
                                        v-if="filteredOptions.length === 0"
                                        class="text-center text-lg font-header pt-5 pb-10"
                                        v-text="
                                            $lang.get(
                                                'No results for your search'
                                            )
                                        "
                                    />
                                </div>
                            </div>
                        </ComboboxOptions>
                    </transition>
                </Teleport>
            </div>
        </div>
    </Combobox>
    <VerticalSlideTransition :show="disabled && !!disabledText">
        <p class="text-input-muted text-sm" v-text="disabledText" />
    </VerticalSlideTransition>
    <InputError class="mt-2" :message="error" />
</template>
<script>
import { ref } from 'vue'
import {
    Combobox,
    ComboboxLabel,
    ComboboxButton,
    ComboboxInput,
    ComboboxOptions,
    ComboboxOption,
} from '@headlessui/vue'
import {
    useFloating,
    autoUpdate,
    autoPlacement,
    offset,
} from '@floating-ui/vue'
import {
    PhCaretDown,
    PhCheck,
    PhMagnifyingGlass,
    PhX,
    PhQuestion,
} from '@phosphor-icons/vue'

import InputLabel from '@/Components/FormElements/InputLabel.vue'
import InputError from '@/Components/FormElements/InputError.vue'
import VerticalSlideTransition from '@/Components/Transitions/VerticalSlideTransition.vue'
import TextTooltip from '@/Components/Elements/TextTooltip.vue'

export default {
    name: 'SearchableSelect',

    emits: ['update:modelValue'],

    components: {
        // Components
        Combobox,
        ComboboxLabel,
        ComboboxButton,
        ComboboxInput,
        ComboboxOptions,
        ComboboxOption,
        InputLabel,
        InputError,
        VerticalSlideTransition,
        TextTooltip,

        // Icons
        PhCaretDown,
        PhCheck,
        PhMagnifyingGlass,
        PhX,
        PhQuestion,
    },

    props: {
        modelValue: {
            type: [null, Number, String, Array],
            required: true,
            validator(value, props) {
                if (props.multiple) {
                    return Array.isArray(value)
                }
                return (
                    value == null ||
                    typeof value == 'number' ||
                    typeof value == 'string'
                )
            },
        },
        multiple: {
            type: Boolean,
            default: false,
        },
        nullable: {
            type: Boolean,
            default: true,
        },
        options: {
            type: Array,
            required: true,
        },
        placeholder: {
            type: String,
            required: false,
        },
        searchPlaceholder: {
            type: String,
            required: false,
        },
        pluralItemName: {
            type: String,
        },
        id: {
            type: String,
            required: true,
        },
        autocomplete: {
            type: String,
            default: 'off',
        },
        label: {
            type: String,
            default: null,
        },
        required: {
            type: Boolean,
            default: true,
        },
        error: {
            type: String,
            default: null,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        disabledText: {
            type: String,
            default: null,
        },
    },

    data: () => ({
        query: '',
        dropDownWidth: 'width: 100%',
    }),

    mounted() {
        this.resizeObserver = new ResizeObserver(this.updateDropdownWidth)
        this.resizeObserver.observe(this.$refs.reference)
    },

    beforeUnmount() {
        if (this.resizeObserver && this.$refs.reference) {
            this.resizeObserver.disconnect()
        }
    },

    computed: {
        hasValue() {
            if (this.multiple) {
                return this.selectedOptions && this.selectedOptions.length > 0
            }
            if (
                this.modelValue === null &&
                this.options.some((opt) => opt.value === null)
            ) {
                return true
            }
            return !!this.modelValue
        },

        /**
         * Get the text to display in the input field.
         * Will display selected items if there are any, if not, then display the query
         */
        inputText() {
            if (this.multiple && this.selectedOptions.length > 0) {
                return this.selectedOptions.length === 1
                    ? this.selectedOptions[0].name
                    : this.$lang.get(':nbr selected', {
                          nbr: this.selectedOptions.length,
                      })
            } else if (!this.multiple && this.hasValue) {
                return this.selectedOptions[0].name
            }
            return this.placeholder || this.$lang.get('Select an option')
        },

        selectedOptions() {
            if (this.multiple) {
                return this.options.filter((option) =>
                    this.modelValue.includes(option.value)
                )
            }
            return this.options.filter(
                (option) => option.value === this.modelValue
            )
        },

        /**
         * Get the options that are not selected and matches the query
         */
        filteredOptions() {
            return this.options.filter(
                (option) =>
                    this.query === '' ||
                    option.name.toLowerCase().includes(this.query.toLowerCase())
            )
        },
    },

    methods: {
        updateValue(value) {
            this.$emit('update:modelValue', value)
            if (!this.multiple) {
                this.query = ''
            }
        },

        deSelect(option_id) {
            this.updateValue(this.modelValue.filter((id) => id !== option_id))
        },

        updateDropdownWidth() {
            this.dropDownWidth =
                'width: ' + this.$refs.reference.offsetWidth + 'px'
        },
    },

    setup() {
        const reference = ref(null)
        const floating = ref(null)
        const floatingArrow = ref(null)

        const { floatingStyles, placement, middlewareData } = useFloating(
            reference,
            floating,
            {
                middleware: [
                    autoPlacement({
                        allowedPlacements: ['top', 'bottom'],
                    }),
                    offset(9),
                ],
                whileElementsMounted: autoUpdate,
            }
        )

        return {
            placement,
            reference,
            floating,
            floatingArrow,
            floatingStyles,
            middlewareData,
        }
    },
}
</script>
